import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { useLicensingContext } from "../../context/useLicensingContext";
import Tooltip from "./Tooltip";

const ImagePerfectCard = ({ label, value, limit, unit }) => {
  const numericValue = Number(String(value || "0").replace(/,/g, "")) || 0;
  const numericLimit = Number(limit);
  const rawPct = numericLimit > 0 ? (numericValue / numericLimit) * 100 : 0;

  const displayPct = rawPct.toFixed(1);
  const barWidth = numericValue > 0 ? Math.max(rawPct, 1) : 0;
  const displayLimit = limit === null ? "unlimited" : numericLimit.toLocaleString();
  const isCritical = rawPct > 90;

  return (
    <Card>
      <TopRow>
        <LeftSide>
          <CardLabel>{label}</CardLabel>
          <PercentBadge $critical={isCritical}>{displayPct}%</PercentBadge>
        </LeftSide>
        
        <RightSide>
          <ValueText>{numericValue.toLocaleString()}</ValueText>
          <LimitText>/ {displayLimit} {unit}</LimitText>
        </RightSide>
      </TopRow>

      <Track>
        <Fill $width={Math.min(barWidth, 100)} $critical={isCritical} />
      </Track>
    </Card>
  );
};

const PRODUCT_CONFIG = {
  meetings: { metrics: [{ key: "tokens", label: "Words", unit: "words" }, { key: "audio_minutes", label: "Audio Minutes", unit: "minutes" }, { key: "video_minutes", label: "Video Minutes", unit: "minutes" }] },
  api: { metrics: [{ key: "tokens", label: "Words", unit: "words" }, { key: "audio_minutes", label: "Audio Minutes", unit: "minutes" }] },
  realtime_api: { metrics: [{ key: "tokens", label: "Words", unit: "words" }, { key: "audio_minutes", label: "Audio Minutes", unit: "minutes" }] },
  video_tool: { metrics: [{ key: "video_minutes", label: "Video Minutes", unit: "minutes" }] },
  presentations: { metrics: [{ key: "tokens", label: "Words", unit: "words" }, { key: "file_uploads", label: "Files Translated", unit: "files" }] },
  website_translation: { metrics: [{ key: "tokens", label: "Words", unit: "words" }] },
  conferences: { metrics: [{ key: "tokens", label: "Words", unit: "words" }, { key: "audio_minutes", label: "Audio Minutes", unit: "minutes" }, { key: "video_minutes", label: "Video Minutes", unit: "minutes" }] },
  emotii_for_teams: { metrics: [{ key: "tokens", label: "Words", unit: "words" }, { key: "audio_minutes", label: "Audio Minutes", unit: "minutes" }] },
  video_translation_api: { metrics: [{ key: "tokens", label: "Words", unit: "words" }, { key: "video_minutes", label: "Video Minutes", unit: "minutes" }, { key: "file_uploads", label: "Files Translated", unit: "files" }] },
  meetings_api: { metrics: [{ key: "tokens", label: "Words", unit: "words" }, { key: "audio_minutes", label: "Audio Minutes", unit: "minutes" }, { key: "video_minutes", label: "Video Minutes", unit: "minutes" }] },
};

const ProductUsageWidget = ({ projectId, productSlug, calendarAction, dateRange }) => {
  const { getProductUsage } = useLicensingContext();
  const [usageData, setUsageData] = useState(null);

  const [rangeStart, rangeEnd] = dateRange || [];

  useEffect(() => {
    if (projectId && productSlug) {
      let params;
      if (rangeStart && rangeEnd) {
        const startDate = new Date(rangeStart);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(rangeEnd);
        endDate.setHours(23, 59, 59, 999);
        params = { start_date: startDate.toISOString(), end_date: endDate.toISOString() };
      }

      getProductUsage(
        projectId,
        productSlug,
        params,
        (response) => {
          setUsageData(response.data || response);
        },
        (error) => {
          console.error("Failed to fetch product usage:", error);
        }
      );
    }
  }, [projectId, productSlug, getProductUsage, rangeStart, rangeEnd]);

  if (!usageData) return null;

  const config = PRODUCT_CONFIG[productSlug];
  if (!config) return null;

  const quota = usageData.quota || {};

  return (
    <SectionWrapper>
      <SectionHeader>
        <SectionTitleGroup>
          <SectionTitle>Usage</SectionTitle>
          <Tooltip
            content="Track your total usage against your subscription limit. Usage is shown for the current month alongside your monthly limit. For a detailed breakdown, refer to the table below. You can also select different time periods to review usage over time."
            side="bottom"
            align="start"
            className="max-w-[280px] leading-relaxed"
          >
            <InfoIconWrapper>
              <Info size={14} />
            </InfoIconWrapper>
          </Tooltip>
        </SectionTitleGroup>
        {calendarAction}
      </SectionHeader>

      <CardsGrid>
        {config.metrics.map((metric) => {
          const data = quota[metric.key] || { used: 0, limit: null };

          return (
            <ImagePerfectCard
              key={metric.key}
              label={metric.label}
              value={data.used}
              limit={data.limit}
              unit={metric.unit}
            />
          );
        })}
      </CardsGrid>
    </SectionWrapper>
  );
};

export default ProductUsageWidget;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 4px;
`;

const SectionTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s;

  &:hover { color: #7f56d9; }
`;

const SectionTitle = styled.h2`
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr); 
  gap: 16px;
  width: 100%;
  align-items: end;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #FFFFFF;
  border: 1px solid #EAECF0;
  border-radius: 10px; 
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #D6BBFB;
    box-shadow: 0px 2px 4px rgba(127, 86, 217, 0.05);
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightSide = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const CardLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #344054;
`;

const PercentBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${(p) => p.$critical ? "#FEE4E2" : "#F4EBFF"};
  color: ${(p) => p.$critical ? "#B42318" : "#6941C6"};
`;

const ValueText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #101828;
`;

const LimitText = styled.span`
  font-size: 13px;
  color: #98A2B3;
  font-weight: 400;
`;

const Track = styled.div`
  width: 100%;
  height: 4px;
  background: #F2F4F7;
  border-radius: 99px;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: ${(p) => p.$width}%;
  border-radius: 99px;
  background: ${(p) => p.$critical ? "#D92D20" : "#7F56D9"};
  transition: width 0.5s ease;
`;